import { z } from 'zod';
/**
 * Wire shape of a tier. `upToUah` is the current field; `upTo` is the LEGACY
 * `ua-2026-01` field in EUR, kept readable so a rule-set document written
 * before this phase still parses. {@link PricingRuleSetSchema} converts it with
 * `fx.uahPerEur` — see `normalizePensionFundTiers`.
 */
const PensionFundTierSchema = z.object({
    upToUah: z.number().nullable().optional(),
    /** @deprecated ua-2026-01 threshold in EUR — migrated on parse. */
    upTo: z.number().nullable().optional(),
    pct: z.number(),
});
/**
 * Ukrainian customs rule set — every rate is data, not code.
 * A new version is a new immutable document; history is never mutated.
 */
export const CustomsRuleSetSchema = z.object({
    version: z.string(), // e.g. "ua-2026-02"
    validFrom: z.coerce.date(),
    dutyPct: z.number(), // 0.10 for ICE; EV: 0 (handled by engine)
    /**
     * Duty rate applied to an EU-origin vehicle covered by a valid EUR.1
     * movement certificate. 0 under the UA–EU free-trade area; a rate rather than
     * a flag, so a future phase-in schedule stays a data edit.
     */
    originEuDutyPct: z.number().default(0),
    exciseBase: z.object({
        // EUR per litre of engine displacement
        petrolSmall: z.number(), // engines up to petrolThresholdL
        petrolLarge: z.number(),
        dieselSmall: z.number(), // engines up to dieselThresholdL
        dieselLarge: z.number(),
        evPerKwh: z.number(), // EUR per kWh of battery capacity
        // EUR per vehicle for a hybrid (Tax Code 215.3.6: €100 per unit). Defaulted
        // so rule sets published before hybrids were priced still parse.
        hybridPerUnit: z.number().default(100),
    }),
    // Displacement thresholds separating small/large excise bases (litres)
    petrolThresholdL: z.number().default(3.0),
    dieselThresholdL: z.number().default(3.5),
    ageCoefCap: z.number(), // excise age multiplier cap (15)
    vatPct: z.number(), // 0.20
    /**
     * Pension fund duty tiers over the customs value in UAH: the first tier whose
     * `upToUah` >= value wins. A legacy `upTo` (EUR) is migrated on parse.
     */
    pensionFundTiers: z.array(PensionFundTierSchema),
    euro6MinYear: z.number().nullable(), // null while Euro-6 enforcement is deferred
});
/**
 * Swiss-side export costs (auction/platform fee + transport to Ukraine).
 *
 * THE PLATFORM FEE HAS TWO PARAMETERIZATIONS, both data (Phase 7):
 *  - `auctionFeePct` + `auctionFeeMinChf` — percentage of the hammer price with
 *    a floor (what alpicar charges today: 2.5%, min CHF 200);
 *  - `exportFeeChf` — a flat per-lot fee (e.g. CARAUKTION CHF 350).
 * `resolveAuctionFeeChf` prefers the percentage form when `auctionFeePct` is
 * set, so switching the commercial model is a rule-set edit and NEVER a code
 * change. Before this phase the 2.5%/200 rule was hardcoded in the API while
 * the calculator used the flat CHF 350 — the two numbers the audit found
 * disagreeing about the same lot.
 */
export const SwissExportFeesSchema = z.object({
    vatPct: z.number(), // 0.081 Swiss VAT where applicable
    exportFeeChf: z.number(), // flat per-lot platform fee, CHF
    /** Percentage platform fee (null = use the flat `exportFeeChf`). */
    auctionFeePct: z.number().min(0).nullable().default(null),
    /** Floor for the percentage platform fee, CHF. */
    auctionFeeMinChf: z.number().min(0).default(0),
    transportEurRange: z.tuple([z.number(), z.number()]), // 1000–1500 EUR EU route
    chfToEurRate: z.number().default(1.05), // configurable FX used for the CHF fee line
});
/**
 * CarsFinders service fees (markup) — configurable per ruleset version.
 *
 * @deprecated since Phase 7 the client-facing commission comes from `plans`
 * (per-tier, `resolvePlanSuccessFee`) so the bid screen, the calculator and the
 * won-car invoice cannot disagree. These two fields are kept ONLY so
 * pre-Phase-7 `pricing_rules` documents still parse; nothing reads them.
 */
export const ServiceFeesSchema = z.object({
    fixedEur: z.number(), // flat brokerage fee
    pctOfBid: z.number(), // share of the winning bid
});
/**
 * FX rates used for cross-currency comparison lines (Ukrainian market prices are USD)
 * and for the UAH thresholds of the pension-fund duty.
 */
export const FxRatesSchema = z.object({
    eurToUsd: z.number().default(1.08),
    /** UAH per EUR — the rate the pension-fund tiers are measured against. */
    uahPerEur: z.number().positive().default(49.0),
});
/**
 * Pricing PLAN TIERS (Phase 14 — B2B portal). Every client is on exactly one
 * tier; `retail` is the default.
 *
 * HOW A TIER IS REACHED depends on the rule set in force:
 *  - `tierRules` published (volume tiers) — the tier is EARNED from paid cars in
 *    a rolling window and recomputed automatically (`resolveVolumeTier`,
 *    volume-tier.ts). A dealer-application approval grants the `dealer` ROLE
 *    only; it does not change the tier.
 *  - `tierRules: null` (every stored document until the owner publishes) — the
 *    pre-volume behaviour stands: approval sets the tier to `dealer`.
 *
 * The tiers are RATES-AS-DATA, versioned inside the same immutable rule-set
 * document as customs/service fees — editing a plan means publishing a new
 * ruleset version, never mutating history.
 */
export const PricingPlanTierSchema = z.enum(['retail', 'dealer', 'dealer_pro']);
export const PRICING_PLAN_TIERS = PricingPlanTierSchema.options;
/** The default tier of a user with no explicit plan. */
export const DEFAULT_PRICING_PLAN_TIER = 'retail';
/**
 * THE publish invariant on the plan grid (volume-tier commission, 2026-10-04).
 *
 * The commission is "a percentage of the winning bid, but not less than the
 * tier minimum". So, per tier:
 *  - the percentage — linear `successFeePct` AND every marginal band — may not
 *    exceed {@link MAX_SUCCESS_FEE_PCT};
 *  - the minimum may not exceed {@link MAX_MIN_FEE_EUR};
 *  - there is NO flat component (`successFeeFlat === 0`) — a flat fee is what
 *    charged EUR 975 on a CHF 1,500 car under the legacy grid;
 *  - there is NO subscription (`monthlyFee === 0`).
 *
 * This replaces the earlier "fee ≤ 8% of the bid on every lot" ceiling: a EUR 500
 * lot paying the EUR 290 retail minimum (58%) is accepted knowingly (owner
 * decision). {@link findPlanShapeViolation} is the machine-checkable form, run by
 * {@link findTariffViolation} behind `PUT /api/admin/pricing/rules` and by the
 * admin tariffs page, so the owner cannot publish a grid that breaks it. Stored
 * documents still PARSE — the rule is checked at publish only.
 */
export const MAX_SUCCESS_FEE_PCT = 0.08;
/** The highest tier minimum fee, in EUR, a publishable grid may carry. */
export const MAX_MIN_FEE_EUR = 300;
/**
 * The hammer price the platform fee is PROBED at before a rule set may be
 * published (Phase 7 re-review, N4) — the top of the admin preview's control
 * prices, in CHF.
 *
 * `resolveAuctionFeeChf` never decreases as the hammer price rises, so a fee of
 * zero here is a fee of zero on every lot. That is the one shape the piecewise
 * checks could not see: `auctionFeePct: 0` with `auctionFeeMinChf: 0` is legal
 * field by field and bills nothing in aggregate.
 */
export const PLATFORM_FEE_PROBE_CHF = 40_000;
/**
 * One marginal band of a tier's success fee, applied like a tax bracket: `pct`
 * is charged on the PORTION of the price inside the band. `upTo: null` is the
 * open-ended top band.
 */
export const MarginalBandSchema = z.object({
    upTo: z.number().positive().nullable(),
    pct: z.number().min(0).max(1),
});
/**
 * Whether a band ladder is well formed — returns a human-readable reason, or
 * null when it is.
 *
 * Why a shape rule is needed at all: {@link marginalFee} walks the bands in
 * order and stops once the price is exhausted, so a ladder whose LAST band is
 * bounded charges nothing above that bound — `[{ upTo: 10000, pct: 0.08 }]` on
 * a 40,000 lot returns 800 and leaves 30,000 untaxed — and a ladder whose
 * bounds do not ascend produces negative widths that silently drop money.
 * {@link findPlanShapeViolation} can see neither: it only bounds each band's
 * rate from ABOVE, and both of these are fees that are too LOW.
 *
 * Enforced twice on purpose: by {@link PricingPlanSchema} (so a malformed
 * ladder cannot even be parsed into a `PricingPlan`) and by
 * {@link findTariffViolation} behind `PUT /api/admin/pricing/rules` (so the
 * refusal names the tier and the reason rather than a zod path).
 */
export function validateBandLadder(bands) {
    if (bands.length === 0)
        return 'the ladder has no bands';
    let previousUpTo = 0;
    for (let index = 0; index < bands.length; index += 1) {
        const band = bands[index];
        if (!band)
            continue;
        const { upTo, pct } = band;
        const isLast = index === bands.length - 1;
        if (pct < 0 || pct > 1) {
            return `band ${index} charges ${pct}, which is not a rate between 0 and 1`;
        }
        if (upTo === null) {
            if (!isLast)
                return `band ${index} is open-ended but is not the last band`;
            continue;
        }
        if (isLast) {
            return `the last band ends at ${upTo}, so every lot above it is charged nothing on the excess`;
        }
        if (upTo <= previousUpTo) {
            return `band ${index} ends at ${upTo}, which is not above the previous bound ${previousUpTo}`;
        }
        previousUpTo = upTo;
    }
    return null;
}
/**
 * Per-tier commercial parameters.
 * - marginalBands: PREFERRED. Tax-bracket fee with no cliff; when present it
 *   fully replaces flat/pct/min/max.
 * - successFeePct/successFeeFlat: legacy linear fee = flat + pct×winningBid
 * - minFee/maxFee: floor/cap on the resolved success fee (maxFee null = uncapped)
 * - biddingPowerCoef: deposit → max-bid multiplier for this tier (retail = ×10)
 * - monthlyFee: recurring subscription (0 for retail/dealer; >0 for dealer_pro)
 */
export const PricingPlanSchema = z.object({
    successFeePct: z.number().min(0),
    successFeeFlat: z.number().min(0),
    minFee: z.number().min(0),
    maxFee: z.number().min(0).nullable(),
    biddingPowerCoef: z.number().min(0),
    monthlyFee: z.number().min(0),
    /** Optional marginal bands; when set they OVERRIDE flat/pct/min/max. */
    marginalBands: z.array(MarginalBandSchema).min(1).optional(),
}).superRefine((plan, ctx) => {
    // A ladder that is merely UNDER-charging is still a tariff nobody signed —
    // and it is invisible to the per-band rate ceiling, which only looks upward.
    if (!plan.marginalBands)
        return;
    const reason = validateBandLadder(plan.marginalBands);
    if (reason) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['marginalBands'],
            message: `Invalid marginal band ladder: ${reason}`,
        });
    }
});
export const PricingPlansSchema = z.object({
    retail: PricingPlanSchema,
    dealer: PricingPlanSchema,
    dealer_pro: PricingPlanSchema,
});
/**
 * Seed plan matrix — "a percentage of the winning bid, but not less than the
 * tier minimum", denominated in EUR (see {@link resolvePlanSuccessFee} on the
 * currency basis). No flat component, no cap, no subscription, no bands:
 *
 *   retail 6% min EUR 290 · dealer 4.5% min EUR 220 · dealer_pro 3% min EUR 160
 *
 * `biddingPowerCoef` (deposit → max bid) is unchanged from the previous grid.
 *
 * SEED ONLY. This grid applies to an EMPTY `pricing_rules` collection. A stored
 * document that carries no `plans` falls back to {@link LEGACY_PRICING_PLANS}
 * instead — an unsigned grid must never become effective by deploy.
 */
export const DEFAULT_PRICING_PLANS = {
    retail: {
        successFeePct: 0.06,
        successFeeFlat: 0,
        minFee: 290,
        maxFee: null,
        biddingPowerCoef: 10,
        monthlyFee: 0,
    },
    dealer: {
        successFeePct: 0.045,
        successFeeFlat: 0,
        minFee: 220,
        maxFee: null,
        biddingPowerCoef: 10,
        monthlyFee: 0,
    },
    dealer_pro: {
        successFeePct: 0.03,
        successFeeFlat: 0,
        minFee: 160,
        maxFee: null,
        biddingPowerCoef: 15,
        monthlyFee: 0,
    },
};
/**
 * The tariff grid ACTUALLY IN FORCE before Phase 7, and the fallback for a
 * stored `pricing_rules` document that carries no `plans` key at all
 * (`ua-2026-01` is exactly such a document).
 *
 * It BREAKS the publish invariant ({@link findPlanShapeViolation}: a flat
 * component and a monthly fee) and charges EUR 975 on a CHF 1,500 car — 65% —
 * and that is deliberate. The invariant this phase establishes is:
 *
 *   NO OWNER-UNSIGNED TARIFF GRID MAY BECOME EFFECTIVE WITHOUT AN EXPLICIT
 *   PUBLISH.
 *
 * Defaulting a legacy document to {@link DEFAULT_PRICING_PLANS} would have put
 * the new, still-unsigned banded grid on live invoices the moment this code
 * deployed — the acceptance criterion left unticked pending the owner's
 * signature would have been decided by a deploy. Defaulting it to THIS grid
 * changes nothing about what production charges today and leaves the 65% defect
 * visible (and loudly warned about, see `warnLegacyPlansFallback`) until a
 * signed grid is published through `PUT /api/admin/pricing/rules`.
 *
 * {@link DEFAULT_PRICING_PLANS} therefore seeds an EMPTY collection only.
 */
export const LEGACY_PRICING_PLANS = {
    retail: {
        successFeePct: 0.05,
        successFeeFlat: 900,
        minFee: 900,
        maxFee: null,
        biddingPowerCoef: 10,
        monthlyFee: 0,
    },
    dealer: {
        successFeePct: 0.03,
        successFeeFlat: 500,
        minFee: 500,
        maxFee: null,
        biddingPowerCoef: 10,
        monthlyFee: 0,
    },
    dealer_pro: {
        successFeePct: 0.02,
        successFeeFlat: 0,
        minFee: 300,
        maxFee: null,
        biddingPowerCoef: 15,
        monthlyFee: 99,
    },
};
/**
 * The platform fee the API CHARGED before Phase 7 — `max(bid × 2.5%, CHF 200)`,
 * hardcoded in `bid-status.service.ts` and `mark-won.service.ts`.
 *
 * A stored `ua-2026-01` document carries NEITHER `auctionFeePct` nor
 * `auctionFeeMinChf`, so without a back-fill {@link resolveAuctionFeeChf} would
 * fall through to that document's flat `exportFeeChf: 350` and silently re-price
 * every lot on deploy: +CHF 150 on a CHF 1,500 lot (an overcharge) and −CHF 275
 * on a CHF 25,000 one (a loss against what alpicar invoices us). The back-fill
 * in `migrateStoredRuleSet` makes the BILLED amount byte-identical to
 * pre-Phase-7 behaviour for a legacy document.
 *
 * The landed-cost QUOTE follows the charge as a result: before this phase the
 * calculator showed a flat CHF 350 while the invoice charged 2.5%/200. That
 * divergence is the defect this phase exists to remove, so the quote moving to
 * match the charge is the intended outcome, not a regression.
 */
export const LEGACY_AUCTION_FEE_PCT = 0.025;
export const LEGACY_AUCTION_FEE_MIN_CHF = 200;
/** Sum of `pct × the portion of amount inside each band`, bracket-style. */
function marginalFee(bands, amount) {
    let remaining = Math.max(0, amount);
    let lowerBound = 0;
    let fee = 0;
    for (const band of bands) {
        if (remaining <= 0)
            break;
        const width = band.upTo === null ? remaining : Math.max(0, band.upTo - lowerBound);
        const slice = Math.min(remaining, width);
        fee += slice * band.pct;
        remaining -= slice;
        if (band.upTo !== null)
            lowerBound = band.upTo;
    }
    return fee;
}
/**
 * Resolve the platform SUCCESS FEE for a winning bid under a plan. Pure.
 *
 * CURRENCY BASIS — EUR, and deliberately only EUR (Phase 7 review, I2).
 * `winningBid` is the hammer price ALREADY CONVERTED at
 * `swissExport.chfToEurRate`, and the fee it returns is in EUR. Every caller
 * uses that one basis: `computeClientDebt` (`bid × chfToEurRate`),
 * `computeLandedCost` (`bidAmountEur`) and the admin preview's control prices.
 * The commission is invoiced in EUR and the minimums and band bounds are EUR
 * amounts, so measuring the fee against anything else would compare two
 * currencies and call the result a share.
 * Surfaces that show the fee MUST name that base next to it («8% of €1,575»),
 * never beside the CHF hammer price on its own — that is what made the admin
 * preview and the bid screen look like they disagreed.
 *
 * With `marginalBands` the fee is the bracket sum — no cliff, and never above
 * the highest band percentage. Without them it falls back to the legacy linear
 * form `clamp(minFee, maxFee, successFeeFlat + successFeePct × max(0, bid))`.
 * A null `maxFee` means uncapped. Negative bids are treated as 0.
 */
export function resolvePlanSuccessFee(plan, winningBid) {
    const bid = Math.max(0, winningBid);
    if (plan.marginalBands && plan.marginalBands.length > 0) {
        return marginalFee(plan.marginalBands, bid);
    }
    const raw = plan.successFeeFlat + plan.successFeePct * bid;
    const floored = Math.max(plan.minFee, raw);
    return plan.maxFee != null ? Math.min(plan.maxFee, floored) : floored;
}
/** Look up a tier's plan, falling back to retail for an unknown/absent tier. */
export function resolvePlanForTier(plans, tier) {
    const parsed = PricingPlanTierSchema.safeParse(tier);
    return plans[parsed.success ? parsed.data : DEFAULT_PRICING_PLAN_TIER];
}
/** A rate as a percentage string without float noise: 0.045 → "4.5%". */
const formatPct = (rate) => `${Number((rate * 100).toFixed(4))}%`;
/** The first rule `plan` breaks — flat, then percentage, then minimum, then monthly fee. */
function planShapeViolation(tier, plan) {
    const ceiling = formatPct(MAX_SUCCESS_FEE_PCT);
    if (plan.successFeeFlat !== 0) {
        return {
            tier,
            code: 'FLAT_FEE_FORBIDDEN',
            message: `Tariff "${tier}" has a flat component of EUR ${plan.successFeeFlat}; the commission ` +
                'must be a percentage with a minimum, with no flat part',
        };
    }
    if (plan.successFeePct > MAX_SUCCESS_FEE_PCT) {
        return {
            tier,
            code: 'FEE_PCT_EXCEEDED',
            message: `Tariff "${tier}" charges ${formatPct(plan.successFeePct)}; the maximum is ${ceiling}`,
        };
    }
    const bands = plan.marginalBands ?? [];
    const richBand = bands.findIndex((band) => band.pct > MAX_SUCCESS_FEE_PCT);
    if (richBand !== -1) {
        return {
            tier,
            code: 'FEE_PCT_EXCEEDED',
            message: `Tariff "${tier}" band ${richBand} charges ${formatPct(bands[richBand]?.pct ?? 0)}; ` +
                `the maximum is ${ceiling}`,
        };
    }
    if (plan.minFee > MAX_MIN_FEE_EUR) {
        return {
            tier,
            code: 'MIN_FEE_EXCEEDED',
            message: `Tariff "${tier}" has a minimum fee of EUR ${plan.minFee}; the maximum is EUR ${MAX_MIN_FEE_EUR}`,
        };
    }
    if (plan.monthlyFee !== 0) {
        return {
            tier,
            code: 'MONTHLY_FEE_FORBIDDEN',
            message: `Tariff "${tier}" charges a monthly fee of EUR ${plan.monthlyFee}; ` +
                'a subscription is not part of the tariff',
        };
    }
    return null;
}
/**
 * The FIRST tier (retail → dealer → dealer_pro) whose plan breaks the publish
 * invariant, and the first rule it breaks (null when the grid is compliant).
 * See {@link MAX_SUCCESS_FEE_PCT} for the rules.
 */
export function findPlanShapeViolation(plans) {
    for (const tier of PRICING_PLAN_TIERS) {
        const violation = planShapeViolation(tier, plans[tier]);
        if (violation)
            return violation;
    }
    return null;
}
/**
 * VOLUME TIERS as data (volume-tier commission, 2026-10-04). A client earns
 * `dealer` / `dealer_pro` from the number of deals that entered `paid` within the
 * last `windowDays`; a tier no longer earned is held for `holdDays` and then drops
 * one step (`resolveVolumeTier`, volume-tier.ts).
 *
 * On the rule set it is `tierRules`, nullable: `null` is today's behaviour (no
 * automatic tiers, approval grants `dealer`) and is ALWAYS publishable — it is the
 * rollback.
 */
export const TierRulesSchema = z.object({
    windowDays: z.number().int().min(1),
    holdDays: z.number().int().min(0),
    thresholds: z.object({ dealer: z.number().int().min(1), dealer_pro: z.number().int().min(1) }),
});
/** Recommended volume tiers: 5 paid cars in 90 days → dealer, 15 → dealer_pro, 90-day hold. */
export const DEFAULT_TIER_RULES = {
    windowDays: 90,
    holdDays: 90,
    thresholds: { dealer: 5, dealer_pro: 15 },
};
/** The first tier whose {@link validateBandLadder} fails (null when all pass). */
export function findBandLadderViolation(plans) {
    for (const tier of PRICING_PLAN_TIERS) {
        const bands = plans[tier].marginalBands;
        if (!bands)
            continue;
        const reason = validateBandLadder(bands);
        if (reason)
            return { tier, reason };
    }
    return null;
}
/**
 * Whether a pension-fund ladder is well formed — same failure mode as a band
 * ladder, in a different unit. `pensionFundPct` (landed-cost.ts) takes the FIRST
 * tier whose `upToUah` covers the value, so a ladder with no open-ended top tier
 * charges 0% above it and a descending ladder skips tiers outright.
 *
 * Checked at PUT rather than in the schema on purpose: a stored document with a
 * degenerate ladder must still PARSE, because an old calculation has to stay
 * reproducible — wrong tariff and all. What must not happen is publishing a new
 * one.
 */
export function findPensionTierViolation(tiers) {
    if (tiers.length === 0)
        return 'the pension-fund ladder has no tiers';
    let previousUpTo = 0;
    for (let index = 0; index < tiers.length; index += 1) {
        const tier = tiers[index];
        if (!tier)
            continue;
        const { upToUah, pct } = tier;
        const isLast = index === tiers.length - 1;
        if (pct < 0 || pct > 1) {
            return `tier ${index} charges ${pct}, which is not a rate between 0 and 1`;
        }
        if (upToUah === null) {
            if (!isLast)
                return `tier ${index} is open-ended but is not the last tier`;
            continue;
        }
        if (isLast) {
            return `the last tier ends at ${upToUah} UAH, so every car above it pays nothing`;
        }
        if (upToUah <= previousUpTo) {
            return `tier ${index} ends at ${upToUah} UAH, which is not above the previous bound ${previousUpTo}`;
        }
        previousUpTo = upToUah;
    }
    return null;
}
/**
 * EVERY commercial reason a rule set may not be published, in one place.
 *
 * The admin tariffs page and `PUT /api/admin/pricing/rules` call THIS, not a
 * subset each: a client-side check that validated less would let the owner press
 * Save on a document the server then refuses, and one that validated more would
 * block a document the server would have accepted. It covers the fields the page
 * can now edit — the plan grid, the volume-tier rules, the platform fee, the FX
 * rate and the pension-fund ladder — because a page that can edit a field but
 * not validate it is how an unpublishable document gets composed.
 */
export function findTariffViolation(rules) {
    const ladder = findBandLadderViolation(rules.plans);
    if (ladder) {
        return {
            code: 'INVALID_BAND_LADDER',
            message: `Tariff "${ladder.tier}" has a malformed band ladder: ${ladder.reason}`,
            tier: ladder.tier,
        };
    }
    const shape = findPlanShapeViolation(rules.plans);
    if (shape)
        return { code: shape.code, message: shape.message, tier: shape.tier };
    // `null` is the rollback and always publishable; a ladder whose top rung is not
    // above the one below it could never promote anyone to dealer_pro.
    const { tierRules } = rules;
    if (tierRules && tierRules.thresholds.dealer_pro <= tierRules.thresholds.dealer) {
        return {
            code: 'INVALID_TIER_RULES',
            message: `tierRules.thresholds.dealer_pro is ${tierRules.thresholds.dealer_pro}, which is not above ` +
                `thresholds.dealer (${tierRules.thresholds.dealer}); Dealer Pro must take more paid cars than Dealer`,
        };
    }
    const pension = findPensionTierViolation(rules.customs.pensionFundTiers);
    if (pension) {
        return { code: 'INVALID_PENSION_LADDER', message: `Pension-fund ladder: ${pension}` };
    }
    const { auctionFeePct, auctionFeeMinChf, exportFeeChf, chfToEurRate } = rules.swissExport;
    if (auctionFeePct !== null && (auctionFeePct < 0 || auctionFeePct > 1)) {
        return {
            code: 'INVALID_PLATFORM_FEE',
            message: `swissExport.auctionFeePct is ${auctionFeePct}, which is not a rate between 0 and 1`,
        };
    }
    if (auctionFeeMinChf < 0 || exportFeeChf < 0) {
        return {
            code: 'INVALID_PLATFORM_FEE',
            message: 'swissExport.auctionFeeMinChf and swissExport.exportFeeChf must not be negative',
        };
    }
    if (!(chfToEurRate > 0)) {
        return {
            code: 'INVALID_PLATFORM_FEE',
            message: `swissExport.chfToEurRate is ${chfToEurRate}; a non-positive FX rate zeroes or inverts every EUR line`,
        };
    }
    if (!(rules.fx.uahPerEur > 0)) {
        return {
            code: 'INVALID_PLATFORM_FEE',
            message: `fx.uahPerEur is ${rules.fx.uahPerEur}; a non-positive rate collapses every pension-fund tier`,
        };
    }
    /**
     * A platform fee that resolves to ZERO at every price (Phase 7 re-review, N4).
     *
     * Each piece passes on its own: `auctionFeePct: 0` IS "a rate between 0 and
     * 1", `auctionFeeMinChf: 0` IS the schema default and IS "not negative".
     * Together they bill CHF 0 on every lot while the auction house still invoices
     * us per lot — a loss on every win, published without a single complaint from
     * the door. The flat parameterization has the same hole with `exportFeeChf: 0`.
     *
     * PROBED rather than enumerated: {@link resolveAuctionFeeChf} is monotone
     * non-decreasing in the hammer price, so a zero at {@link
     * PLATFORM_FEE_PROBE_CHF} — the top of the admin preview's control prices — is
     * a zero at every price below it too, and a future third parameterization is
     * covered by construction instead of by remembering to extend a list.
     */
    if (!(resolveAuctionFeeChf(rules.swissExport, PLATFORM_FEE_PROBE_CHF) > 0)) {
        return {
            code: 'INVALID_PLATFORM_FEE',
            message: `swissExport charges NO platform fee on any lot (auctionFeePct=${auctionFeePct}, ` +
                `auctionFeeMinChf=${auctionFeeMinChf}, exportFeeChf=${exportFeeChf}); the auction ` +
                'house still invoices a fee per lot, so this grid loses money on every win',
        };
    }
    return null;
}
/**
 * THE auction/platform fee for a hammer price, in CHF — the ONE function both
 * `computeClientDebt` (what the client is billed) and `computeLandedCost` (what
 * the calculator shows) call, so the two can no longer disagree.
 */
export function resolveAuctionFeeChf(fees, bidChf) {
    const bid = Math.max(0, bidChf);
    if (fees.auctionFeePct != null) {
        return Math.max(bid * fees.auctionFeePct, fees.auctionFeeMinChf);
    }
    return Math.max(fees.exportFeeChf, 0);
}
/**
 * Resale MARKETPLACE fees (Phase 18 — RATES AS DATA). A consignment sale is
 * charged `consignmentFeePct` of the final sale price, capped at
 * `consignmentFeeCap` (null = uncapped); an optional flat `listingFee` may be
 * charged up-front (BaT-style). Own-stock sales carry NO consignment fee — the
 * platform is the seller. Editing rates means publishing a NEW ruleset version,
 * never mutating history.
 *
 * These are rates only — the fee is COMPUTED here but never settled. Any ledger
 * write stays behind the Phase-13 `PAYMENTS_ENABLED` gate.
 */
export const MarketplaceFeesSchema = z.object({
    /** Consignment success fee as a share of the final sale price (5–10%). */
    consignmentFeePct: z.number().min(0).max(1),
    /** Cap on the resolved consignment fee (null = uncapped). */
    consignmentFeeCap: z.number().min(0).nullable(),
    /** Optional flat up-front listing fee (0 = none). */
    listingFee: z.number().min(0).default(0),
});
/** Seed marketplace fees — 7% consignment, capped, no up-front listing fee. */
export const DEFAULT_MARKETPLACE_FEES = {
    consignmentFeePct: 0.07,
    consignmentFeeCap: 5000,
    listingFee: 0,
};
/**
 * The full versioned pricing rule set stored in the `pricing_rules` collection.
 * Always parse through {@link PricingRuleSetSchema}, which normalizes the
 * legacy EUR pension tiers into UAH.
 */
const PricingRuleSetBaseSchema = z.object({
    customs: CustomsRuleSetSchema,
    swissExport: SwissExportFeesSchema,
    serviceFees: ServiceFeesSchema,
    fx: FxRatesSchema,
    /**
     * Phase 14 B2B plan tiers. `.default()` keeps every pre-Phase-14 ruleset
     * document (and the byte-compatible web route) valid.
     *
     * The default is {@link LEGACY_PRICING_PLANS}, NOT the Phase-7 seed: a stored
     * document with no `plans` key must keep charging exactly what it charged
     * yesterday. Inheriting the new banded grid on parse would have made an
     * owner-unsigned tariff effective on the first deploy — see the invariant on
     * `LEGACY_PRICING_PLANS`. `migrateStoredRuleSet` warns loudly whenever this
     * default is what a document lands on.
     */
    plans: PricingPlansSchema.default(LEGACY_PRICING_PLANS),
    /**
     * Volume tiers (see {@link TierRulesSchema}). Defaults to `null` so a stored
     * document without the key keeps today's tier behaviour (approval grants
     * `dealer`, nothing is recomputed) until a rule set carrying `tierRules` is
     * published. Publishing `tierRules: null` is the rollback.
     */
    tierRules: TierRulesSchema.nullable().default(null),
    /**
     * Phase 18 resale-marketplace fees. `.default()` keeps every pre-Phase-18
     * ruleset document valid — an old doc parses and inherits the seed fees.
     */
    marketplace: MarketplaceFeesSchema.default(DEFAULT_MARKETPLACE_FEES),
});
/**
 * Migrate a rule set's pension tiers to UAH thresholds.
 *
 * `ua-2026-01` documents carry `upTo` in EUR; multiplying by the document's own
 * `fx.uahPerEur` reproduces the tiering that document meant at the rate it was
 * written under, so an old stored calculation stays reproducible. A tier that
 * already carries `upToUah` is passed through untouched.
 */
function normalizePensionFundTiers(tiers, uahPerEur) {
    return tiers.map((tier) => {
        if (tier.upToUah !== undefined)
            return { upToUah: tier.upToUah, pct: tier.pct };
        if (tier.upTo !== undefined) {
            return { upToUah: tier.upTo === null ? null : tier.upTo * uahPerEur, pct: tier.pct };
        }
        // Neither bound given — treat as the open-ended tier rather than dropping it.
        return { upToUah: null, pct: tier.pct };
    });
}
/**
 * The document that fell back to the legacy grid, named loudly.
 *
 * Deliberately `console.warn` and deliberately on EVERY parse: this is the one
 * signal that a deployment is billing under a grid nobody published, and a
 * once-per-process log is exactly the kind that is missing from the container
 * an operator actually looks at. Parses are rare (once per fetch on the client,
 * once per win on the server), so the cost is a log line, not a loop.
 */
function warnLegacyPlansFallback(version) {
    const label = typeof version === 'string' ? version : 'unknown';
    console.warn(`[pricing-rules] rule set "${label}" has no \`plans\` key: falling back to ` +
        'LEGACY_PRICING_PLANS (retail 5% + 900 flat, floored at 900) — the grid that was in ' +
        'force before Phase 7. That grid BREAKS the publish invariant (FLAT_FEE_FORBIDDEN: ' +
        'no flat component, at most ' +
        `${MAX_SUCCESS_FEE_PCT * 100}% and a minimum of at most EUR ${MAX_MIN_FEE_EUR}): it ` +
        'charges EUR 975 on a CHF 1,500 lot, a 65% share. The seed grid is NOT applied ' +
        'implicitly — publish a signed grid through PUT /api/admin/pricing/rules.');
}
/**
 * Bring a STORED document up to the shape the current code reads — BEFORE zod's
 * own defaults get to put a different number in.
 *
 * Only one thing is back-filled here, and only when the document says nothing at
 * all about it: the platform fee. `ua-2026-01` carries neither `auctionFeePct`
 * nor `auctionFeeMinChf`, and letting those default to `null`/`0` would drop
 * {@link resolveAuctionFeeChf} onto that document's flat `exportFeeChf: 350` —
 * a fee the API never charged. Back-filling {@link LEGACY_AUCTION_FEE_PCT} /
 * {@link LEGACY_AUCTION_FEE_MIN_CHF} keeps the billed amount byte-identical to
 * pre-Phase-7 behaviour. A document that mentions EITHER field has made a
 * deliberate choice and is left untouched, which is also what makes this
 * idempotent: a re-parse sees both fields and does nothing.
 *
 * The missing `plans` key is NOT back-filled here — its default lives on the
 * field ({@link LEGACY_PRICING_PLANS}) — but it is WARNED about here, because
 * this is the only place that can still tell "absent" from "explicitly set".
 */
function migrateStoredRuleSet(raw) {
    if (raw === null || typeof raw !== 'object' || Array.isArray(raw))
        return raw;
    const document = raw;
    if (!('plans' in document)) {
        const customs = document.customs;
        warnLegacyPlansFallback(customs !== null && typeof customs === 'object'
            ? customs.version
            : undefined);
    }
    const swissExport = document.swissExport;
    if (swissExport === null || typeof swissExport !== 'object' || Array.isArray(swissExport)) {
        return document;
    }
    const fees = swissExport;
    if ('auctionFeePct' in fees || 'auctionFeeMinChf' in fees)
        return document;
    return {
        ...document,
        swissExport: {
            ...fees,
            auctionFeePct: LEGACY_AUCTION_FEE_PCT,
            auctionFeeMinChf: LEGACY_AUCTION_FEE_MIN_CHF,
        },
    };
}
export const PricingRuleSetSchema = z
    .preprocess(migrateStoredRuleSet, PricingRuleSetBaseSchema)
    .transform((raw) => ({
    ...raw,
    customs: {
        ...raw.customs,
        pensionFundTiers: normalizePensionFundTiers(raw.customs.pensionFundTiers, raw.fx.uahPerEur),
    },
}));
/**
 * Seed rule set "ua-2026-02".
 *
 * Serves two purposes:
 * 1. Fallback when the `pricing_rules` collection is empty or unreachable —
 *    the calculator always works, marked as a preliminary estimate.
 * 2. Seed document inserted on first read of GET /api/pricing/rules/current
 *    (both the Next.js route and the NestJS controller lazy-seed this).
 *
 * What changed from `ua-2026-01` (Phase 7):
 *  - pension-fund tiers moved from EUR to the statutory UAH thresholds, read
 *    against `fx.uahPerEur`;
 *  - `originEuDutyPct: 0` makes the EUR.1 relief a rate rather than a hardcoded
 *    `return 0` inside a web-only service;
 *  - the platform fee is seeded in its PERCENTAGE form (2.5%, min CHF 200) —
 *    the rule that was hardcoded in the API before this phase — so no live
 *    number moves on deploy. The flat `exportFeeChf: 350` stays in the document
 *    as the alternative parameterization the owner can switch to.
 *
 * MIGRATION: a production `pricing_rules` collection already holds a
 * `ua-2026-01` document, and `getActiveRuleSet` serves the NEWEST one in force —
 * this seed only applies to an EMPTY collection. Publishing `ua-2026-02` to an
 * existing deployment is an explicit act through the admin tariffs page
 * (PUT /api/admin/pricing/rules), which can now edit every field the migration
 * needs: the plan grid, `swissExport.auctionFeePct`/`auctionFeeMinChf`,
 * `fx.uahPerEur` and `customs.pensionFundTiers`.
 *
 * What the stored `ua-2026-01` document does in the meantime:
 *  - platform fee — back-filled to 2.5%/min CHF 200 by `migrateStoredRuleSet`,
 *    i.e. byte-identical to what the API charged before Phase 7;
 *  - tariff grid — falls back to {@link LEGACY_PRICING_PLANS} with a loud
 *    warning, i.e. byte-identical to what it charged before Phase 7;
 *  - volume tiers — `tierRules` parses as `null` (this seed carries
 *    {@link DEFAULT_TIER_RULES}), i.e. the tier behaviour does not change either;
 *  - pension tiers / EUR.1 — migrated on parse, reproducing the tiering the
 *    document itself meant.
 * Nothing in this seed reaches a live invoice until someone publishes it.
 *
 * ENV: none. This phase adds no environment variable; every rate is data in the
 * `pricing_rules` collection.
 *
 * Pending legal review the whole calculator is labelled «попередній розрахунок»
 * in the UI.
 */
export const DEFAULT_PRICING_RULES = PricingRuleSetSchema.parse({
    customs: {
        version: 'ua-2026-02',
        validFrom: new Date('2026-01-01T00:00:00.000Z'),
        dutyPct: 0.1,
        originEuDutyPct: 0,
        exciseBase: {
            petrolSmall: 50,
            petrolLarge: 100,
            dieselSmall: 75,
            dieselLarge: 150,
            evPerKwh: 1,
            hybridPerUnit: 100,
        },
        petrolThresholdL: 3.0,
        dieselThresholdL: 3.5,
        ageCoefCap: 15,
        vatPct: 0.2,
        pensionFundTiers: [
            { upToUah: 549_120, pct: 0.03 },
            { upToUah: 965_120, pct: 0.04 },
            { upToUah: null, pct: 0.05 },
        ],
        euro6MinYear: null,
    },
    swissExport: {
        vatPct: 0.081,
        exportFeeChf: 350,
        auctionFeePct: 0.025,
        auctionFeeMinChf: 200,
        transportEurRange: [1000, 1500],
        chfToEurRate: 1.05,
    },
    serviceFees: {
        fixedEur: 900,
        pctOfBid: 0,
    },
    fx: {
        eurToUsd: 1.08,
        uahPerEur: 49.0,
    },
    plans: DEFAULT_PRICING_PLANS,
    tierRules: DEFAULT_TIER_RULES,
    marketplace: DEFAULT_MARKETPLACE_FEES,
});
//# sourceMappingURL=pricing-rules.js.map